/**
 * Task numbers (commit ↔ task links, slice 1). Pure and DETERMINISTIC: the
 * plan depends only on the merged board, never on array order or on which
 * device runs it, so two devices that backfill or repair the same board write
 * identical values and field-level last-write-wins converges.
 *
 * 1. a task with a valid number keeps it; on a duplicate the earliest
 *    (createdAt, id) keeps it and the others lose it;
 * 2. tasks without a number, in (createdAt, id) order, get max + 1, max + 2, …
 *    where max is the highest of the board's `taskSeq` and the kept numbers;
 * 3. `taskSeq` (the high-water mark) is the highest number in use, so a
 *    deleted task's number is never handed out again.
 */
import { derivePrefix, formatTaskKey, validPrefix } from './taskKeys';

const validNumber = (n) => Number.isInteger(n) && n > 0;

const byAge = (a, b) => {
  const ca = String(a.createdAt || '');
  const cb = String(b.createdAt || '');
  if (ca !== cb) return ca < cb ? -1 : 1;
  const ia = String(a.id);
  const ib = String(b.id);
  return ia < ib ? -1 : ia > ib ? 1 : 0;
};

/**
 * @returns {{ numbers: Map<string, number>, changed: Map<string, number>, taskSeq: number }}
 *   numbers: every task id → its number; changed: only the ones that differ from what is stored.
 */
export function planTaskNumbers(board) {
  const tasks = (board?.tasks || []).filter((t) => t && t.id != null).slice().sort(byAge);
  const numbers = new Map();
  const holders = new Set();
  const pool = [];
  for (const t of tasks) {
    if (validNumber(t.number) && !holders.has(t.number)) {
      holders.add(t.number);
      numbers.set(String(t.id), t.number);
    } else pool.push(t);
  }
  const stored = validNumber(board?.taskSeq) ? board.taskSeq : 0;
  let next = Math.max(stored, 0, ...holders);
  for (const t of pool) {
    next += 1;
    numbers.set(String(t.id), next);
  }
  const changed = new Map();
  for (const t of tasks) {
    const n = numbers.get(String(t.id));
    if (t.number !== n) changed.set(String(t.id), n);
  }
  return { numbers, changed, taskSeq: next };
}

/** The number a NEW task on `board` gets (never below the high-water mark). */
export function nextTaskNumber(board) {
  return planTaskNumbers(board).taskSeq + 1;
}

/** The board's prefix: the stored one, else the one its name would get. */
export function boardPrefixOf(board, taken = []) {
  return validPrefix(board?.keyPrefix) || derivePrefix(board?.name, taken);
}

/** task id → key ("APB-42") for every task of `board`, planned numbers included. */
export function taskKeysFor(board, taken = []) {
  const prefix = boardPrefixOf(board, taken);
  const { numbers } = planTaskNumbers(board);
  const keys = new Map();
  for (const [id, n] of numbers) keys.set(id, formatTaskKey(prefix, n));
  return keys;
}

/**
 * The board with every task numbered (missing or duplicate numbers planned
 * in) and `taskSeq` at the highest number. Returns `board` itself when
 * nothing changes.
 */
export function fillTaskNumbers(board) {
  if (!board) return board;
  const { changed, taskSeq } = planTaskNumbers(board);
  const seqChanged = taskSeq > 0 && taskSeq !== board.taskSeq;
  if (!changed.size && !seqChanged) return board;
  return {
    ...board,
    ...(seqChanged ? { taskSeq } : {}),
    tasks: changed.size ? board.tasks.map((t) => (changed.has(String(t.id)) ? { ...t, number: changed.get(String(t.id)) } : t)) : board.tasks,
  };
}

/**
 * The board after the whole fill: numbers (fillTaskNumbers) and the prefix
 * when it has none yet. Returns `board` itself when nothing changes, so a
 * caller can tell "nothing to write" by identity.
 */
export function withTaskNumbers(board, taken = []) {
  if (!board) return board;
  const numbered = fillTaskNumbers(board);
  if (validPrefix(board.keyPrefix)) return numbered;
  return { ...numbered, keyPrefix: derivePrefix(board.name, taken) };
}

/**
 * A new task on `board`: the board with its numbers filled first (so the
 * planned keys of older, not yet numbered tasks do not shift) and the number
 * the new task gets.
 */
export function boardForNewTask(board) {
  const filled = fillTaskNumbers(board);
  const number = (filled.taskSeq || 0) + 1;
  return { board: { ...filled, taskSeq: number }, number };
}

/** The task on `board` whose number is `n` (stored or planned), or null. */
export function taskByNumber(board, n) {
  const { numbers } = planTaskNumbers(board);
  for (const [id, num] of numbers) if (num === n) return (board.tasks || []).find((t) => String(t.id) === id) || null;
  return null;
}
